import { createContext, useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PaywallModal } from '../../components/PaywallModal'

export const PaywallContext = createContext(null)

const CHECKOUT_TAB = {
  PLANS: 'planos',
  PACKAGES: 'pacotes',
}

const getLocationPath = (location) =>
  `${location.pathname}${location.search}${location.hash}`

const normalizeReturnTo = (returnTo, fallback) => {
  if (!returnTo || typeof returnTo !== 'string') {
    return fallback
  }

  try {
    const url = new URL(returnTo, window.location.origin)

    if (url.origin !== window.location.origin) {
      return fallback
    }

    return `${url.pathname}${url.search}${url.hash}` || fallback
  } catch {
    return fallback
  }
}

export const PaywallProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [paywallState, setPaywallState] = useState({
    isOpen: false,
    reason: null,
    returnTo: null,
    onDismiss: null,
  })

  const closePaywall = useCallback(() => {
    setPaywallState((current) => ({
      ...current,
      isOpen: false,
      onDismiss: null,
    }))
  }, [])

  const openPaywall = useCallback(
    (config = {}) => {
      const currentPath = getLocationPath(location)
      const returnTo = normalizeReturnTo(config.returnTo, currentPath)

      setPaywallState({
        isOpen: true,
        reason: config.reason ?? null,
        returnTo,
        onDismiss: config.onDismiss ?? null,
      })
    },
    [location],
  )

  const redirectToCheckout = useCallback(
    (tab) => {
      const currentPath = getLocationPath(location)
      const returnTo = paywallState.returnTo || currentPath
      const params = new URLSearchParams({
        tab,
        returnTo,
      })

      if (paywallState.reason) {
        params.set('paywallReason', paywallState.reason)
      }

      setPaywallState((current) => ({
        ...current,
        isOpen: false,
        onDismiss: null,
      }))
      navigate(`/PlanCheckoutUser?${params.toString()}`)
    },
    [location, navigate, paywallState.reason, paywallState.returnTo],
  )

  const value = useMemo(
    () => ({
      isPaywallOpen: paywallState.isOpen,
      openPaywall,
      closePaywall,
    }),
    [closePaywall, openPaywall, paywallState.isOpen],
  )

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PaywallModal
        isOpen={paywallState.isOpen}
        onClose={closePaywall}
        onDismiss={paywallState.onDismiss}
        onSelectPlans={() => redirectToCheckout(CHECKOUT_TAB.PLANS)}
        onSelectPackages={() => redirectToCheckout(CHECKOUT_TAB.PACKAGES)}
      />
    </PaywallContext.Provider>
  )
}
